import { useRef, useEffect, type FC, useState } from 'react';
import { type LexicalEditor } from 'lexical';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { INSERT_EMOJI_COMMAND } from '../plugins/EmojiPickerPlugin';
import EmojiList from '../plugins/EmojiList.json';
import { EVENT_KEY } from '../../types/enums';
import Dialog from './DialogPopup';
import styles from './SubComponent.module.scss';
import { sanitizeUrl } from './URLValidation';

const TablePopup: FC<{ activeEditor: LexicalEditor; onClose: () => void }> = ({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}) => {
  const hasModifier = useRef(false);
  const [rowXCol, setRowXCol] = useState({ row: '', col: '' });
  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const handleOnClick = () => {
    if (Number(rowXCol.col) && Number(rowXCol.row)) {
      activeEditor.dispatchCommand(INSERT_TABLE_COMMAND, {
        columns: rowXCol.col,
        rows: rowXCol.row,
      });
      onClose();
    }
  };
  return (
    <Dialog>
      <input
        value={rowXCol.row}
        onChange={(e) => setRowXCol((prevState) => ({ ...prevState, row: e.target.value }))}
        data-testid="tablepopup-input-row"
      />
      <input
        value={rowXCol.col}
        onChange={(e) => setRowXCol((prevState) => ({ ...prevState, col: e.target.value }))}
        data-testid="tablepopup-input-col"
      />
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOnClick();
        }}
        data-testid="tablepopup-confirm-btn"
      >
        Confirm
      </button>
    </Dialog>
  );
};
const LinkPopup: FC<{ activeEditor: LexicalEditor; onClose: () => void }> = ({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}) => {
  const hasModifier = useRef(false);
  const [linkUrl, setLinkUrl] = useState('');
  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const handleOnClick = () => {
    activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(linkUrl));
    onClose();
  };
  return (
    <Dialog>
      <input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid="linkpoup-input-url" />
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleOnClick();
        }}
        data-testid="linkpoup-confirm-btn"
      >
        Confirm
      </button>
    </Dialog>
  );
};
const EmojiPopup: FC<{ activeEditor: LexicalEditor; onClose: () => void }> = ({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}) => {
  const hasModifier = useRef(false);
  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const handleOnClick = (emoji: { description: string; emoji: string; type: string; as: string[]; tags: string[] }) => {
    activeEditor.dispatchCommand(INSERT_EMOJI_COMMAND, { emoji: emoji.emoji });
    onClose();
  };
  return (
    <Dialog>
      <section className={styles['emoji-popover']}>
        {EmojiList.map((emoji) => (
          <button
            key={emoji.description}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleOnClick(emoji);
            }}
            onKeyDown={(e) => e.key === EVENT_KEY.ENTER && handleOnClick(emoji)}
            tabIndex={0}
            className={styles['emoji-body']}
            data-testid="emoji-btns"
          >
            {emoji.emoji}
          </button>
        ))}
      </section>
    </Dialog>
  );
};

export { TablePopup, LinkPopup, EmojiPopup };
